import clsx from 'clsx';
import React, { HTMLAttributes } from 'react';
import { Card, CardContent, CardHeader, CardActions } from '../card';
import { Banner } from '../banner';
import QuickLink from '../../basic/quickLink';
import Button from '../../basic/button';
import { DataAnalyticsLocations, DataAnalyticsTypes, buildAnalytics } from '../../basic/analytics';

interface ArticleBannerProps extends HTMLAttributes<HTMLButtonElement> {
  banner: any;
  card: any;
  bannerStyle: any;
  parentAnalyticsLocation?: DataAnalyticsLocations;
}

const ArticleBanner = ({
  banner,
  card,
  bannerStyle,
  parentAnalyticsLocation = DataAnalyticsLocations.Page,
}: ArticleBannerProps): JSX.Element => {
  const buttonDataAnalytics = buildAnalytics(
    DataAnalyticsLocations.Card,
    DataAnalyticsTypes.Button,
    [parentAnalyticsLocation, DataAnalyticsLocations.Banner],
  );

  return (
    <Banner
      className={`mb-10 ${card.theme} ${bannerStyle === 'with_floating_card' ? 'lg:mx-36 xl:mx-36 p-6 flex-col-reverse' : ''}`}
      media={banner?.src}
      layout={banner?.layout}
      cardLayout={card?.tileLayout}
      cardStyle="lg:absolute lg:self-center"
      enableMobileStack={true}
      data-testid="ArticleBanner"
      mediaStyle={`${
        banner?.layout === 'left-facing'
          ? 'rounded-t-md xl:rounded-tl-none xl:rounded-tr-md xl:rounded-br-md'
          : banner?.layout === 'right-facing'
            ? 'rounded-t-md xl:rounded-tr-none xl:rounded-tl-md xl:rounded-bl-md'
            : ''
      } lg:w-4/12 xl:w-4/12 aspect-w-16 sm:aspect-w-16 lg:aspect-w-16 h-5xl lg:h-8xl`}
      alt={banner?.src}
      style={{
        flexDirection:
          bannerStyle === 'with_floating_card'
            ? ''
            : banner?.layout === 'left-facing'
              ? 'row'
              : 'row-reverse',
      }}
    >
      {card && (
        <Card
          theme={card.theme}
          className={clsx(
            bannerStyle === 'with_floating_card'
              ? 'flex-col rounded-t-none space-y-md w-full lg:w-8/12 xl:w-8/12 lg:px-8 px-md lg:h-8xl justify-center'
              : 'justify-center flex-col rounded-t-none w-full lg:w-8/12 xl:w-8/12 lg:px-24 xl:px-24 px-md xl:py-2xl py-lg px-md h-5xl lg:h-8xl',
          )}
        >
          {card?.header && (
            <CardHeader
              header={card.header}
              className={`${bannerStyle === 'with_floating_card' ? 'text-secondary-400 lg:text-3xl xl:text-3xl md:text-2xl text-xl font-light' : 'lg:text-5xl xl:text-5xl md:text-2xl text-xl font-normal text-center'}`}
            />
          )}
          {card?.description && (
            <CardContent
              description={card.description}
              className={`${bannerStyle === 'with_floating_card' ? 'lg:text-sm xl:text-sm text-xs [&_p]:mb-4' : 'lg:text-3xl xl:text-3xl text-3xl lg:block xl:block font-light'}`}
            />
          )}
          {card?.navigation && (
            <CardActions className="md:flex flex-row sm:self-center lg:self-center xl:self-center">
              {card?.navigation &&
                card?.navigation.map((btn: any, idx: number) => (
                    <Button
                      link={btn.url}
                      key={idx}
                      shape="rounded"
                      dataAnalytics={buttonDataAnalytics}
                      border={true}
                      className="p-sm px-6 text-sm bg-btn-fill text-btn-base border-btn-border hover:bg-btn-hover hover:text-btn-hover-text w-full mb-2 md:w-auto md:mr-2"
                    >
                      {btn.title}
                    </Button>
                ))}
            </CardActions>
          )}
        </Card>
      )}
    </Banner>
  );
};

export default ArticleBanner;
